/**
 * /admin/ops/crons — Cron Jobs (SCRUM-313): KPIs por período, agregación
 * GROUP BY job (no fetch-all-y-contar en cliente), "Requieren atención" y
 * detalle por job con historial paginado.
 */
import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { OpsSubnav } from "@/components/OpsSubnav";
import { CRON_DOMAIN_LABELS, type CronDomain } from "@/lib/cron-registry";

interface CronRun {
  id: string;
  jobId: string;
  status: string;
  message: string | null;
  durationMs: number | null;
  executedAt: string;
}

interface JobSummary {
  jobId: string;
  label: string;
  domain: CronDomain;
  total: number;
  errors: number;
  lastStatus: string | null;
  lastMessage: string | null;
  lastExecutedAt: string | null;
  avgDurationMs: number | null;
}

interface Kpis {
  totalRuns: number;
  totalErrors: number;
  jobsWithErrors: number;
  jobsNeverRunInPeriod: number;
}

const STATUS_BADGE: Record<string, string> = {
  success: "bg-green-100 text-green-800",
  ok: "bg-green-100 text-green-800",
  error: "bg-red-100 text-red-800",
  running: "bg-blue-100 text-blue-800",
  warning: "bg-yellow-100 text-yellow-800",
};

const PERIOD_LABELS: Record<string, string> = { "24h": "24 horas", "7d": "7 días", "30d": "30 días" };
const HISTORY_PAGE_SIZE = 20;

function fmtDur(ms: number | null) {
  if (ms === null) return "—";
  return ms >= 1000 ? `${(ms / 1000).toFixed(1)}s` : `${ms}ms`;
}

function fmtDate(iso: string | null) {
  if (!iso) return "—";
  return new Date(iso).toLocaleString("es-AR", { dateStyle: "short", timeStyle: "medium" });
}

export const Route = createFileRoute("/admin/ops/crons")({
  component: CronsPage,
});

function KpiTile({ label, value, tone }: { label: string; value: number; tone?: "warn" }) {
  return (
    <div className="rounded-lg border p-3">
      <p className="text-[11px] uppercase tracking-wide text-muted-foreground">{label}</p>
      <p className={`text-xl font-semibold mt-0.5 ${tone === "warn" && value > 0 ? "text-amber-600" : ""}`}>{value}</p>
    </div>
  );
}

function CronsPage() {
  const [period, setPeriod] = useState<"24h" | "7d" | "30d">("7d");
  const [jobs, setJobs] = useState<JobSummary[]>([]);
  const [kpis, setKpis] = useState<Kpis | null>(null);
  const [loading, setLoading] = useState(false);
  const [attentionOnly, setAttentionOnly] = useState(false);

  const [detailJob, setDetailJob] = useState<JobSummary | null>(null);
  const [history, setHistory] = useState<CronRun[]>([]);
  const [historyOffset, setHistoryOffset] = useState(0);
  const [historyHasMore, setHistoryHasMore] = useState(false);
  const [historyLoading, setHistoryLoading] = useState(false);

  async function load() {
    setLoading(true);
    try {
      const res = await fetch(`/api/admin/cron-runs?summary=1&period=${period}`, { headers: { accept: "application/json" } });
      const data = await res.json();
      setJobs(data.jobs ?? []);
      setKpis(data.kpis ?? null);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { void load(); }, [period]); // eslint-disable-line react-hooks/exhaustive-deps

  async function loadHistory(jobId: string, offset: number) {
    setHistoryLoading(true);
    try {
      const params = new URLSearchParams({ jobId, limit: String(HISTORY_PAGE_SIZE), offset: String(offset) });
      const res = await fetch(`/api/admin/cron-runs?${params}`, { headers: { accept: "application/json" } });
      const data = await res.json();
      setHistory((prev) => (offset === 0 ? data.runs ?? [] : [...prev, ...(data.runs ?? [])]));
      setHistoryHasMore(Boolean(data.hasMore));
      setHistoryOffset(offset);
    } finally {
      setHistoryLoading(false);
    }
  }

  function openDetail(job: JobSummary) {
    setDetailJob(job);
    setHistory([]);
    setHistoryOffset(0);
    void loadHistory(job.jobId, 0);
  }

  const filtered = useMemo(() => (attentionOnly ? jobs.filter((j) => j.errors > 0) : jobs), [jobs, attentionOnly]);

  const grouped = useMemo(() => {
    const byDomain = new Map<CronDomain, JobSummary[]>();
    for (const j of filtered) {
      const arr = byDomain.get(j.domain) ?? [];
      arr.push(j);
      byDomain.set(j.domain, arr);
    }
    return Array.from(byDomain.entries()).sort((a, b) => a[0].localeCompare(b[0]));
  }, [filtered]);

  return (
    <div className="p-6 space-y-4 max-w-5xl mx-auto">
      <OpsSubnav hint="Corridas agregadas de sm_cron_runs por job y dominio." />
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h1 className="text-xl font-semibold">Cron Jobs</h1>
          <p className="text-sm text-muted-foreground">
            Agregación por job sobre corridas reales de <code>sm_cron_runs</code> — período {PERIOD_LABELS[period]}.
          </p>
        </div>
        <div className="flex items-center gap-2">
          {(["24h", "7d", "30d"] as const).map((p) => (
            <button
              key={p}
              onClick={() => setPeriod(p)}
              className={`px-3 py-1.5 text-xs rounded-full border ${period === p ? "bg-primary text-primary-foreground" : "hover:bg-muted"}`}
            >
              {PERIOD_LABELS[p]}
            </button>
          ))}
          <button
            onClick={load}
            disabled={loading}
            className="px-4 py-2 text-sm rounded-md bg-primary text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
          >
            {loading ? "Cargando…" : "Refrescar"}
          </button>
        </div>
      </div>

      {kpis ? (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
          <KpiTile label="Corridas" value={kpis.totalRuns} />
          <KpiTile label="Errores" value={kpis.totalErrors} tone="warn" />
          <KpiTile label="Jobs con errores" value={kpis.jobsWithErrors} tone="warn" />
          <KpiTile label="Sin corridas en el período" value={kpis.jobsNeverRunInPeriod} />
        </div>
      ) : null}

      <button
        onClick={() => setAttentionOnly((v) => !v)}
        className={`px-3 py-1.5 text-xs rounded-md border font-medium ${attentionOnly ? "border-amber-500/50 bg-amber-500/10 text-amber-700" : "text-muted-foreground"}`}
      >
        ⚠ Requieren atención {kpis ? `(${kpis.jobsWithErrors})` : ""}
      </button>

      {grouped.length === 0 && !loading && (
        <div className="text-center py-12 text-muted-foreground text-sm">Sin jobs para este filtro.</div>
      )}

      {grouped.map(([domain, domainJobs]) => (
        <div key={domain} className="space-y-2">
          <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            {CRON_DOMAIN_LABELS[domain]} <span className="font-normal">({domainJobs.length})</span>
          </h3>
          <div className="overflow-x-auto rounded-lg border">
            <table className="w-full text-sm">
              <thead className="bg-muted/40">
                <tr>
                  <th className="text-left p-3 font-medium">Job</th>
                  <th className="text-left p-3 font-medium">Corridas</th>
                  <th className="text-left p-3 font-medium">Errores</th>
                  <th className="text-left p-3 font-medium">Últ. estado</th>
                  <th className="text-left p-3 font-medium">Últ. ejecución</th>
                  <th className="text-left p-3 font-medium">Dur. prom.</th>
                  <th className="text-right p-3 font-medium">Acciones</th>
                </tr>
              </thead>
              <tbody>
                {domainJobs.map((j) => (
                  <tr key={j.jobId} className="border-t hover:bg-muted/20">
                    <td className="p-3">
                      <p className="font-medium">{j.label}</p>
                      <p className="font-mono text-[11px] text-muted-foreground">{j.jobId}</p>
                    </td>
                    <td className="p-3 tabular-nums">{j.total}</td>
                    <td className="p-3 tabular-nums">
                      <span className={j.errors > 0 ? "text-red-600 font-medium" : ""}>{j.errors}</span>
                    </td>
                    <td className="p-3">
                      {j.lastStatus ? (
                        <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${STATUS_BADGE[j.lastStatus] ?? "bg-gray-100 text-gray-700"}`}>
                          {j.lastStatus}
                        </span>
                      ) : (
                        <span className="text-xs text-muted-foreground">—</span>
                      )}
                    </td>
                    <td className="p-3 text-xs text-muted-foreground whitespace-nowrap">{fmtDate(j.lastExecutedAt)}</td>
                    <td className="p-3 tabular-nums text-xs">{fmtDur(j.avgDurationMs)}</td>
                    <td className="p-3 text-right">
                      <button
                        onClick={() => openDetail(j)}
                        className="px-3 py-1 text-xs rounded-md border hover:bg-muted"
                      >
                        Ver historial
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ))}

      <Dialog open={!!detailJob} onOpenChange={(open) => { if (!open) setDetailJob(null); }}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          {detailJob ? (
            <>
              <DialogHeader>
                <DialogTitle>{detailJob.label}</DialogTitle>
              </DialogHeader>
              <p className="font-mono text-xs text-muted-foreground -mt-2">{detailJob.jobId}</p>

              {history.length === 0 && !historyLoading ? (
                <p className="text-sm text-muted-foreground py-6 text-center">Sin corridas registradas.</p>
              ) : (
                <div className="overflow-x-auto rounded-lg border">
                  <table className="w-full text-sm">
                    <thead className="bg-muted/40">
                      <tr>
                        <th className="text-left p-2 font-medium">Estado</th>
                        <th className="text-left p-2 font-medium">Duración</th>
                        <th className="text-left p-2 font-medium">Ejecutado</th>
                        <th className="text-left p-2 font-medium">Mensaje</th>
                      </tr>
                    </thead>
                    <tbody>
                      {history.map((r) => (
                        <tr key={r.id} className="border-t">
                          <td className="p-2">
                            <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${STATUS_BADGE[r.status] ?? "bg-gray-100 text-gray-700"}`}>
                              {r.status}
                            </span>
                          </td>
                          <td className="p-2 tabular-nums text-xs">{fmtDur(r.durationMs)}</td>
                          <td className="p-2 text-xs text-muted-foreground whitespace-nowrap">{fmtDate(r.executedAt)}</td>
                          <td className="p-2 text-xs text-muted-foreground max-w-xs truncate">{r.message ?? "—"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {historyHasMore ? (
                <button
                  onClick={() => void loadHistory(detailJob.jobId, historyOffset + HISTORY_PAGE_SIZE)}
                  disabled={historyLoading}
                  className="w-full text-xs rounded-md border py-2 hover:bg-muted disabled:opacity-50"
                >
                  {historyLoading ? "Cargando…" : "Cargar más"}
                </button>
              ) : null}
            </>
          ) : null}
        </DialogContent>
      </Dialog>
    </div>
  );
}
